// Verdeckte Terminanfrage an mehrere Empfänger.
//
// WICHTIG zur Abgrenzung: Eine Outlook-Einladung (Exchange-Attendees) kennt KEIN Bcc — dort
// sehen sich alle Eingeladenen. Wer mehrere fragen will, ohne sie einander zu zeigen
// („Wer kann Dienstag 10 Uhr?"), verschickt deshalb eine normale E-Mail mit Bcc. Diese Datei
// liefert die Textbausteine dafür; die Teilnehmerliste des Termins bleibt davon unberührt.

export interface AnfrageTermin {
  titel?: string | null;
  beschreibung?: string | null;
  start_datum: string;
  end_datum?: string | null;
  ganztaegig?: boolean | null;
  objekt?: { bezeichnung?: string | null; strasse?: string | null; hausnummer?: string | null; plz?: string | null; stadt?: string | null } | null;
}

const datumLang = (iso: string, ganztaegig: boolean): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-DE', {
    weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric',
    ...(ganztaegig ? { timeZone: 'UTC' } : {}),
  });
};

const uhr = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

export function objektAdresse(o: AnfrageTermin['objekt']): string {
  if (!o) return '';
  const zeile1 = `${o.strasse || ''} ${o.hausnummer || ''}`.trim();
  const zeile2 = `${o.plz || ''} ${o.stadt || ''}`.trim();
  return [zeile1, zeile2].filter(Boolean).join(', ');
}

/**
 * Platzhalter für die Vorlage „Termin-Einladung" (ki_trigger termin_einladung) — dieselben
 * Schlüssel wie im Outlook-Weg (graph-kalender-sync/einladung.ts), damit EINE Vorlage beide
 * Wege bedient.
 */
export function terminAnfragePlatzhalter(t: AnfrageTermin): Record<string, string> {
  const ganztaegig = !!t.ganztaegig;
  return {
    'termin.titel': (t.titel || '').trim(),
    'termin.beschreibung': (t.beschreibung || '').trim(),
    'termin.datum': datumLang(t.start_datum, ganztaegig),
    'termin.uhrzeit': ganztaegig ? 'Ganztägig' : `${uhr(t.start_datum)} Uhr`,
    'termin.ende': ganztaegig || !t.end_datum ? '' : `${uhr(t.end_datum)} Uhr`,
    'objekt.bezeichnung': (t.objekt?.bezeichnung || '').trim(),
    'objekt.adresse': objektAdresse(t.objekt),
  };
}

/** Betreff der Anfrage: Titel plus Datum, damit sie im Postfach erkennbar ist. */
export function anfrageBetreff(t: AnfrageTermin): string {
  const titel = (t.titel || 'Terminanfrage').trim();
  const datum = datumLang(t.start_datum, !!t.ganztaegig);
  return datum ? `${titel} – ${datum}` : titel;
}

/** Fallback-Text, falls die Vorlage „Termin-Einladung" fehlt oder inaktiv ist. */
export function anfrageText(t: AnfrageTermin): string {
  const p = terminAnfragePlatzhalter(t);
  const zeilen = [
    'Guten Tag,',
    '',
    'wir möchten folgenden Termin mit Ihnen abstimmen:',
    '',
    `Termin: ${p['termin.titel'] || '—'}`,
    `Datum: ${p['termin.datum']}${p['termin.uhrzeit'] ? `, ${p['termin.uhrzeit']}` : ''}${p['termin.ende'] ? ` – ${p['termin.ende']}` : ''}`,
  ];
  if (p['objekt.bezeichnung'] || p['objekt.adresse']) {
    zeilen.push(`Objekt: ${[p['objekt.bezeichnung'], p['objekt.adresse']].filter(Boolean).join(', ')}`);
  }
  if (p['termin.beschreibung']) zeilen.push('', p['termin.beschreibung']);
  zeilen.push('', 'Bitte geben Sie uns kurz Rückmeldung, ob der Termin passt.', '', 'Mit freundlichen Grüßen');
  return zeilen.join('\n');
}

// ── Buchungslink an mehrere ──────────────────────────────────────────────────

export interface AnfrageBuchungslink {
  titel?: string | null;
  beschreibung?: string | null;
  slot_dauer_minuten?: number | null;
}

export function buchungBetreff(l: AnfrageBuchungslink): string {
  return (l.titel || '').trim() || 'Terminvorschlag';
}

/** Mailtext mit dem öffentlichen Buchungslink — jeder Empfänger wählt selbst einen Slot. */
export function buchungText(l: AnfrageBuchungslink, url: string): string {
  const zeilen = ['Guten Tag,', ''];
  const dauer = l.slot_dauer_minuten ? ` (${l.slot_dauer_minuten} Minuten)` : '';
  zeilen.push(`bitte wählen Sie über den folgenden Link einen freien Termin${dauer}:`, '', url);
  if ((l.beschreibung || '').trim()) zeilen.push('', (l.beschreibung || '').trim());
  zeilen.push('', 'Der gewählte Termin wird direkt bestätigt.', '', 'Mit freundlichen Grüßen');
  return zeilen.join('\n');
}
